<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  turn: { type: Object, required: true },
});

defineEmits(['quickReply']);

const { t } = useI18n();

const isUser = computed(() => props.turn.role === 'user');
const isError = computed(() => props.turn.role === 'error');

// An agent turn can come back empty: Meta then explains itself through the
// reason fields instead, and showing a blank bubble would hide that.
const reasons = computed(() =>
  [
    props.turn.handoff_reason &&
      t('META_AGENT.PLAYGROUND.HANDOFF_REASON', {
        reason: props.turn.handoff_reason,
      }),
    props.turn.no_response_reason &&
      t('META_AGENT.PLAYGROUND.NO_RESPONSE_REASON', {
        reason: props.turn.no_response_reason,
      }),
  ].filter(Boolean)
);

const body = computed(() => props.turn.text || props.turn.agent_response || '');
</script>

<template>
  <div
    class="flex flex-col gap-2 max-w-[80%]"
    :class="isUser ? 'self-end items-end' : 'self-start items-start'"
  >
    <div
      class="px-4 py-2 text-sm break-words whitespace-pre-wrap rounded-xl"
      :class="{
        'bg-n-brand text-white': isUser,
        'bg-n-solid-2 text-n-slate-12': !isUser && !isError,
        'bg-n-ruby-2 text-n-ruby-11 border border-n-ruby-6': isError,
      }"
    >
      <span v-if="body">{{ body }}</span>
      <span v-else class="italic text-n-slate-11">
        {{ t('META_AGENT.PLAYGROUND.NO_REPLY') }}
      </span>
    </div>

    <div v-if="reasons.length" class="flex flex-col gap-1">
      <span
        v-for="reason in reasons"
        :key="reason"
        class="px-2 py-1 text-xs rounded-md bg-n-amber-2 text-n-amber-11"
      >
        {{ reason }}
      </span>
    </div>

    <div v-if="turn.quick_replies?.length" class="flex flex-wrap gap-1">
      <button
        v-for="reply in turn.quick_replies"
        :key="reply"
        type="button"
        class="px-2 py-1 text-xs border rounded-md border-n-weak text-n-slate-11 hover:text-n-slate-12"
        @click="$emit('quickReply', reply)"
      >
        {{ reply }}
      </button>
    </div>
  </div>
</template>
